import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import magnifying from '../../assets/images/042-market-research1.svg';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import {
  TypeCategories,
  saveSoa,
  selectApplicabilityStatus,
  selectControlGroups,
  selectSelectedFrameworkTitle,
  selectSoaPdfData,
  selectSoas,
} from '../../redux/reducers/frameworkReducer';
import { selectSelectedOrganization } from '../../redux/reducers/organizationReducer';
import formatDate from '../../utils/dateHelpers';
import SOAControlList from './SOAControlList';
import { downloadPdf } from './SoaPDF';

function SOAControls() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const soas = useSelector(selectSoas);
  const organization = useSelector(selectSelectedOrganization);
  const frameworkTitle = useSelector(selectSelectedFrameworkTitle);
  const [showOlder, setShowOlder] = useState(false);
  const [initiateDownload, setInitiateDownload] = useState(false);
  const [selectedSoa, setSelectedSoa] = useState(soas[0]);
  const controlGroups = useSelector((state) => selectControlGroups(state, TypeCategories.OPERATIONAL));
  const completionPercentage = useSelector(selectApplicabilityStatus);
  const groupTitles = useSelector((state) => controlGroups.map((group) => selectTitle(state, group.id)));
  const pdfData = useSelector((state) => selectSoaPdfData(state, selectedSoa));

  const getSoaInfo = (soa) => ({
    unfinishedControls: soa.unfinishedControls.length,
    version: soa.version,
    created: soa.created,
    completionPercentage: soa.completionPercentage,
  });

  const toggleShowOlder = () => {
    setShowOlder(!showOlder);
  };

  const handleGenerateNew = () => {
    dispatch(saveSoa(completionPercentage));
  };

  const handleDownload = (soa) => {
    setSelectedSoa(soa);
    setInitiateDownload(true);
  };

  const getSoaTitle = (soa) => (
    `SOA - v${soa.version} (${formatDate(soa.created)})`
  );

  const getIncompleteLabel = (length) => {
    const labelKey = length === 1 ? 'controlIsIncomplete' : 'controlsAreIncomplete';
    const label = `${t('framework.soa.notValid')} - ${length} ${t(`framework.soa.${labelKey}`)}`;
    return label;
  };

  useEffect(() => {
    if (selectedSoa && initiateDownload) {
      const soaInfo = getSoaInfo(selectedSoa);
      downloadPdf({ item: pdfData, groups: groupTitles, soaInfo, frameworkTitle, organizationName: organization.name });
    }
    setInitiateDownload(false);
  }, [selectedSoa, initiateDownload]);

  return (
    <Container variant="controlBaseView" disableGutters maxWidth={false}>
      <Box sx={{ maxWidth: '1200px', mt: '24px', overflowY: 'auto' }}>
        <Stack spacing={2}>
          <Typography variant="tabHeader">{t('framework.soa.title')}</Typography>
          <Typography variant="viewDesc">{t('framework.soa.description')}</Typography>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Typography variant="soaTitle">{t('framework.soa.latest')}</Typography>
            <Button
              id="generate-soa"
              variant="contained"
              subtype="soa"
              onClick={handleGenerateNew}
              endIcon={<AddIcon />}
            >
              <Typography variant="buttonMedium">{t('framework.soa.buttons.generate')}</Typography>
            </Button>
          </Stack>
          {soas.length > 0 ? (
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ height: 56, px: '16px', py: '10px', bgcolor: 'rgba(46, 175, 152, 0.06)' }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="soaVersion">
                    {getSoaTitle(soas[0])}
                  </Typography>
                  {(soas[0].completionPercentage || 0) < 100 && (
                    <Chip
                      variant="controlChip"
                      color="warning"
                      label={getIncompleteLabel(soas[0].unfinishedControls.length)}
                    />
                  )}
                </Stack>
                <Button
                  id="download-latest-soa"
                  variant="contained"
                  subtype="soa"
                  onClick={() => handleDownload(soas[0])}
                  endIcon={<PrintOutlinedIcon />}
                >
                  <Typography variant="buttonMedium">{t('framework.soa.buttons.print')}</Typography>
                </Button>
              </Stack>
              {soas.length > 1 && (
                <Card variant="controlDetails">
                  <CardContent sx={{ padding: 0 }}>
                    <Stack direction="row" alignItems="center">
                      <Typography variant="soaVersion">{t('framework.soa.older')}</Typography>
                      <IconButton id="show-older-soa" onClick={toggleShowOlder}>
                        {showOlder ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Stack>
                  </CardContent>
                  <Collapse in={showOlder} unmountOnExit>
                    <CardContent sx={{ padding: 0 }}>
                      <Stack spacing={1.5}>
                        {soas.slice(1).map((soa) => (
                          <Stack
                            key={soa.version}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ height: 56, px: '16px', py: '10px', bgcolor: 'rgba(46, 175, 152, 0.06)' }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="soaVersion">
                                {getSoaTitle(soa)}
                              </Typography>
                              {(soa.completionPercentage || 0) < 100 && (
                                <Chip
                                  variant="controlChip"
                                  color="warning"
                                  label={getIncompleteLabel(soa.unfinishedControls.length)}
                                />
                              )}
                            </Stack>
                            <Button
                              id="download-older-soa"
                              variant="contained"
                              subtype="soa"
                              onClick={() => handleDownload(soa)}
                              endIcon={<PrintOutlinedIcon />}
                            >
                              <Typography variant="buttonMedium">{t('framework.soa.buttons.print')}</Typography>
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                  </Collapse>
                </Card>
              )}
            </Stack>
          ) : (
            <Stack>
              <Typography variant="noDocuments">{t('framework.soa.noDocuments')}</Typography>
              <Stack sx={{ my: '16px' }}>
                <img src={magnifying} width="100px" alt="Empty icon" />
              </Stack>
            </Stack>
          )}
          <SOAControlList />
        </Stack>
      </Box>
    </Container>
  );
}

export default SOAControls;
